import React, { useEffect, useMemo, useState } from "react";
import { Briefcase, Search as MagIcon, Loader2, Sparkles, IndianRupee, GraduationCap, AlertTriangle, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

// -----------------------------------------------------------------------------
// Jobs Awareness — a *rare-jobs* catalog for seekers who don't see themselves
// in the usual banking/software list. Shop assistants, RSA techs, commission
// agents, influencers, riders, drivers, drone pilots, hyperlocal tutors etc.
// -----------------------------------------------------------------------------
const CAT_LABEL = {
  retail:"Retail", transport:"Transport", sales:"Sales", content:"Content · Media",
  construction:"Construction", trade:"Trade", gig:"Gig Delivery",
  pet_care:"Pet care", remote:"Remote", events:"Events",
  education:"Education", legal:"Legal", agri:"Agriculture", tech:"Tech",
};
const VIBE_LABEL = {
  low_barrier:"Low barrier · start today", quick_earning:"Quick earning",
  skill_based:"Skill-based", high_variance:"High variance", long_term:"Long-term",
  variable_income:"Variable income",
};

export default function Jobs() {
  const { user } = useAuth();
  const [meta, setMeta] = useState({ categories:[], vibes:[] });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("");
  const [vibe, setVibe] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  useSeo({ title:"Rare & real jobs · Awareness for seekers · find2near",
           description:"Beyond the usual jobs — Shop Assistant, RSA, Commission Agent, Influencer, Content Creator, Building Contractor, Rider, Driver, Drone Pilot, Pet Caretaker & more.",
           canonical:"/jobs" });

  useEffect(()=>{ api.get("/jobs/awareness/meta").then(r=>setMeta(r.data)).catch(()=>{}); }, []);
  useEffect(()=>{
    setLoading(true);
    const p = {};
    if (cat) p.category = cat;
    if (vibe) p.vibe = vibe;
    if (q) p.q = q;
    api.get("/jobs/awareness", { params:p }).then(r=>setItems(r.data)).catch(()=>setItems([])).finally(()=>setLoading(false));
  }, [cat, vibe]);   // eslint-disable-line

  const showInterest = async (slug) => {
    if (!user) { toast.error("Sign in to save interest"); return; }
    try { await api.post(`/jobs/awareness/${slug}/interest`); toast.success("Interest saved — we'll surface local leads soon."); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-6" data-testid="jobs-page">
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><Briefcase size={12} className="text-orange-600"/> Employment awareness</div>
        <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">Real work. <span className="text-orange-600">Rarely listed.</span></h1>
        <p className="text-stone-500 text-sm mt-1">Shop assistant · RSA tech · Commission agent · Influencer · Content creator · Building contractor · Rider · Driver · Drone pilot · and more.</p>
      </div>

      <form onSubmit={e=>{e.preventDefault(); api.get("/jobs/awareness", { params:{ q } }).then(r=>setItems(r.data));}} className="bg-white border border-stone-200 rounded-3xl p-3 flex flex-wrap gap-2 items-center mb-4">
        <div className="flex items-center gap-2 flex-1 min-w-[200px] bg-stone-50 rounded-2xl px-3 py-1.5">
          <MagIcon size={14} className="text-stone-400"/>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search — 'driver', 'canva', 'drone'…" className="bg-transparent w-full outline-none text-sm" data-testid="jobs-q"/>
        </div>
        <Select value={cat||"__all__"} onValueChange={v=>setCat(v==="__all__"?"":v)}>
          <SelectTrigger className="rounded-full h-9 w-52 text-xs" data-testid="jobs-cat"><SelectValue placeholder="All categories"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All categories</SelectItem>
            {(meta.categories||[]).map(c=><SelectItem key={c.key} value={c.key}>{CAT_LABEL[c.key]||c.key} · {c.count}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={vibe||"__all__"} onValueChange={v=>setVibe(v==="__all__"?"":v)}>
          <SelectTrigger className="rounded-full h-9 w-56 text-xs" data-testid="jobs-vibe"><SelectValue placeholder="Any vibe"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Any vibe</SelectItem>
            {(meta.vibes||[]).map(v=><SelectItem key={v.key} value={v.key}>{VIBE_LABEL[v.key]||v.key}</SelectItem>)}
          </SelectContent>
        </Select>
      </form>

      {loading ? <div className="text-center py-16 text-stone-400"><Loader2 className="mx-auto animate-spin"/></div>
      : items.length===0 ? <div className="text-center py-16 text-stone-400 border border-dashed border-stone-200 rounded-3xl">No roles match this filter.</div>
      : (
        <div className="grid gap-2">
          {items.map(j=>{
            const isOpen = open===j.slug;
            return (
              <article key={j.slug} className="rounded-3xl bg-white border border-stone-200 hover:border-orange-300 transition-all" data-testid={`job-${j.slug}`}>
                <button onClick={()=>setOpen(isOpen?null:j.slug)} className="w-full text-left p-4 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 bg-orange-100 text-orange-800">{CAT_LABEL[j.category]||j.category}</span>
                      <span className="text-[10px] font-bold rounded-full px-2 py-0.5 bg-stone-100 text-stone-700">{VIBE_LABEL[j.vibe]||j.vibe}</span>
                    </div>
                    <div className="font-black text-stone-900">{j.title}</div>
                    <div className="text-xs text-emerald-700 font-bold mt-0.5 inline-flex items-center gap-1"><IndianRupee size={10}/> {j.earning}</div>
                  </div>
                  <ChevronRight size={16} className={`text-stone-400 mt-1 transition-transform ${isOpen?"rotate-90":""}`}/>
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 -mt-1 border-t border-stone-100 pt-3 space-y-2">
                    {j.skills?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1"><GraduationCap size={10} className="inline mr-1"/> Skills you need</div>
                        <div className="flex flex-wrap gap-1">{j.skills.map(s=><span key={s} className="text-[11px] font-bold bg-stone-100 rounded-full px-2 py-0.5">{s}</span>)}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1"><Sparkles size={10} className="inline mr-1"/> How to start</div>
                      <p className="text-sm text-stone-700 leading-relaxed">{j.how_to_start}</p>
                    </div>
                    {j.watch_for && (
                      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-2 text-xs text-amber-900 flex items-start gap-2">
                        <AlertTriangle size={12} className="mt-0.5 shrink-0"/> <div><b>Watch for:</b> {j.watch_for}</div>
                      </div>
                    )}
                    {j.sample_platforms?.length > 0 && (
                      <div className="text-xs text-stone-600">
                        <span className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mr-1">Start on:</span>
                        {j.sample_platforms.join(" · ")}
                      </div>
                    )}
                    <Button onClick={()=>showInterest(j.slug)} className="rounded-full bg-orange-600 hover:bg-orange-700 font-black h-9" data-testid={`job-interest-${j.slug}`}>I'm interested — send me local leads</Button>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
      <div className="mt-8 text-[11px] text-stone-500 text-center">
        This is awareness content — not a job placement service. Do your own due diligence before joining anyone.
      </div>
    </div>
  );
}
